/**
 * KonvaCanvas - Main canvas class that manages the Konva stage directly
 * This replaces react-konva with direct Konva.js manipulation for better performance
 */

import Konva from 'konva';
import {
  CanvasOptions,
  ViewportTransform,
  CanvasEventType,
  CanvasEventCallback,
  Point,
  IRenderer,
} from './types';
import { EventEmitter } from './EventEmitter';
import { ViewportManager, ViewportManagerOptions } from './ViewportManager';
import { EditableElement } from '../editing/types';

export interface KonvaCanvasOptions extends CanvasOptions {
  viewportOptions?: ViewportManagerOptions;
}

/**
 * KonvaCanvas manages the entire canvas system including:
 * - Stage and layer management
 * - Viewport (zoom/pan/touch)
 * - Element rendering
 * - Event delegation to React
 */
export class KonvaCanvas {
  // Core Konva objects
  private stage: Konva.Stage;
  private layer: Konva.Layer;
  private backgroundRect: Konva.Rect;
  
  // Container element
  private container: HTMLDivElement;
  
  // Managers
  private viewportManager: ViewportManager;
  
  // Event emitter for React communication
  private emitter: EventEmitter;
  
  // Element renderers map (elementId -> renderer)
  private renderers: Map<string, IRenderer> = new Map();
  
  // Canvas dimensions
  private width: number;
  private height: number;
  private backgroundColor: string;
  
  // Dotted background pattern
  private backgroundPattern: HTMLCanvasElement | null = null;

  constructor(container: HTMLDivElement, options: KonvaCanvasOptions) {
    this.container = container;
    this.width = options.width;
    this.height = options.height;
    this.backgroundColor = options.backgroundColor || '#F5F5F5';
    
    // Use provided emitter or create a new one
    this.emitter = options.emitter || new EventEmitter();
    
    // Create stage
    this.stage = new Konva.Stage({
      container: this.container,
      width: this.width,
      height: this.height,
      draggable: false, // Disable stage dragging - we handle panning via ViewportManager
    });
    
    // Create main layer
    this.layer = new Konva.Layer();
    this.stage.add(this.layer);
    
    // Create dotted background
    this.backgroundPattern = this.createDottedPattern();
    this.backgroundRect = new Konva.Rect({
      x: -this.width * 5,
      y: -this.height * 5,
      width: this.width * 10,
      height: this.height * 10,
      fillPatternImage: this.backgroundPattern as unknown as HTMLImageElement,
      fillPatternRepeat: 'repeat',
      // Scale pattern to maintain visual size (pattern is 8x resolution)
      fillPatternScale: { x: 0.125, y: 0.125 },
      listening: false,
    });
    this.layer.add(this.backgroundRect);
    
    // Initialize viewport manager
    this.viewportManager = new ViewportManager(
      this.stage,
      this.layer,
      this.container,
      this.emitter,
      options.viewportOptions
    );
    
    // Setup stage styling
    this.stage.container().style.touchAction = 'none';
    
    // Draw initial state
    this.layer.batchDraw();
    
    // Emit ready event
    setTimeout(() => {
      this.emitter.emit('ready', {
        fitToContent: this.fitToContent.bind(this),
        zoomControls: this.viewportManager.getZoomControls(),
        transform: this.viewportManager.getTransform(),
      });
    }, 0);
  }

  /**
   * Create dotted background pattern with sharp square shapes
   * Uses very high resolution (8x) to maintain sharpness at all zoom levels
   */
  private createDottedPattern(): HTMLCanvasElement {
    const dotSpacing = 20;
    // Use very high resolution (8x) to maintain sharpness even when zoomed in
    const scale = 8;
    const patternCanvas = document.createElement('canvas');
    patternCanvas.width = dotSpacing * scale;
    patternCanvas.height = dotSpacing * scale;
    
    const ctx = patternCanvas.getContext('2d', { 
      willReadFrequently: false,
      alpha: true 
    });
    if (ctx) {
      // Disable image smoothing for sharp, crisp angular shapes
      ctx.imageSmoothingEnabled = false;
      // Also disable on the canvas element itself
      (ctx as any).webkitImageSmoothingEnabled = false;
      (ctx as any).mozImageSmoothingEnabled = false;
      (ctx as any).msImageSmoothingEnabled = false;
      
      // Draw sharp square shape at high resolution
      // Increased size: 3px at base resolution = 24px at 8x scale for better visibility
      const dotSize = 2.5 * scale; // Larger square size
      ctx.fillStyle = '#CCCCCC';
      
      // Draw a sharp square at the top-left corner (0, 0)
      // Using integer coordinates for pixel-perfect rendering
      ctx.fillRect(0, 0, dotSize, dotSize);
    }
    
    return patternCanvas;
  }

  // ============================================================================
  // EVENT SYSTEM
  // ============================================================================

  /**
   * Subscribe to canvas events
   */
  on<T extends CanvasEventType>(event: T, callback: CanvasEventCallback<T>): () => void {
    return this.emitter.on(event, callback);
  }

  /**
   * Subscribe to canvas event once
   */
  once<T extends CanvasEventType>(event: T, callback: CanvasEventCallback<T>): () => void {
    return this.emitter.once(event, callback);
  }

  /**
   * Unsubscribe from canvas events
   */
  off<T extends CanvasEventType>(event: T, callback: CanvasEventCallback<T>): void {
    this.emitter.off(event, callback);
  }

  // ============================================================================
  // ELEMENT MANAGEMENT
  // ============================================================================

  /**
   * Add an element to the canvas
   */
  addElement(element: EditableElement, renderer: IRenderer): Konva.Node {
    this.renderers.set(element.id, renderer);
    const node = renderer.render();
    node.id(element.id);
    this.layer.add(node as Konva.Group);
    this.layer.batchDraw();
    return node;
  }

  /**
   * Update an element
   */
  updateElement(id: string, updates: Partial<EditableElement>): void {
    const renderer = this.renderers.get(id);
    if (renderer) {
      renderer.update(updates);
      this.layer.batchDraw();
    }
  }

  /**
   * Remove an element from the canvas
   */
  removeElement(id: string): void {
    const renderer = this.renderers.get(id);
    if (renderer) {
      renderer.destroy();
      this.renderers.delete(id);
      this.layer.batchDraw();
    }
  }

  /**
   * Get a renderer by element ID
   */
  getRenderer(id: string): IRenderer | undefined {
    return this.renderers.get(id);
  }

  /**
   * Get Konva node by element ID
   */
  getNode(id: string): Konva.Node | undefined {
    return this.layer.findOne(`#${id}`);
  }

  /**
   * Get element bounds in canvas coordinates (directly from Konva)
   * This is the source of truth for visual positioning
   */
  getElementBounds(id: string): { x: number; y: number; width: number; height: number } | null {
    const node = this.getNode(id);
    if (!node) return null;
    
    const rect = node.getClientRect({ relativeTo: this.layer });
    return {
      x: rect.x,
      y: rect.y,
      width: rect.width,
      height: rect.height,
    };
  }

  /**
   * Get element screen position (transformed by viewport)
   * Use this for positioning UI elements like toolbars
   */
  getElementScreenPosition(id: string): { x: number; y: number; width: number; height: number } | null {
    const bounds = this.getElementBounds(id);
    if (!bounds) return null;
    
    const transform = this.getTransform();
    return {
      x: bounds.x * transform.scale + transform.x,
      y: bounds.y * transform.scale + transform.y,
      width: bounds.width * transform.scale,
      height: bounds.height * transform.scale,
    };
  }

  /**
   * Get multiple elements' combined screen bounds (for toolbar positioning)
   */
  getElementsScreenBounds(ids: string[]): { x: number; y: number; width: number; height: number; centerX: number; centerY: number } | null {
    if (ids.length === 0) return null;
    
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    let validCount = 0;
    
    for (const id of ids) {
      const bounds = this.getElementBounds(id);
      if (bounds) {
        minX = Math.min(minX, bounds.x);
        minY = Math.min(minY, bounds.y);
        maxX = Math.max(maxX, bounds.x + bounds.width);
        maxY = Math.max(maxY, bounds.y + bounds.height);
        validCount++;
      }
    }
    
    if (validCount === 0) return null;
    
    const transform = this.getTransform();
    const screenX = minX * transform.scale + transform.x;
    const screenY = minY * transform.scale + transform.y;
    const screenWidth = (maxX - minX) * transform.scale;
    const screenHeight = (maxY - minY) * transform.scale;
    
    return {
      x: screenX,
      y: screenY,
      width: screenWidth,
      height: screenHeight,
      centerX: screenX + screenWidth / 2,
      centerY: screenY + screenHeight / 2,
    };
  }

  /**
   * Get all element IDs
   */
  getElementIds(): string[] {
    return Array.from(this.renderers.keys());
  }

  /**
   * Clear all elements
   */
  clearElements(): void {
    this.renderers.forEach((renderer) => {
      renderer.destroy();
    });
    this.renderers.clear();
    this.layer.batchDraw();
  }

  // ============================================================================
  // VIEWPORT CONTROLS
  // ============================================================================

  /**
   * Get current viewport transform
   */
  getTransform(): ViewportTransform {
    return this.viewportManager.getTransform();
  }

  /**
   * Set viewport transform
   */
  setTransform(transform: Partial<ViewportTransform>): void {
    this.viewportManager.setTransform(transform);
  }

  /**
   * Zoom in
   */
  zoomIn(): void {
    this.viewportManager.zoomIn();
  }

  /**
   * Zoom out
   */
  zoomOut(): void {
    this.viewportManager.zoomOut();
  }

  /**
   * Toggle fullscreen
   */
  fullscreen(): void {
    this.viewportManager.fullscreen();
  }

  /**
   * Fit content to view
   */
  fitToContent(): void {
    this.viewportManager.fitToContent();
  }

  /**
   * Enable or disable panning
   */
  setPanningDisabled(disabled: boolean): void {
    this.viewportManager.setPanningDisabled(disabled);
  }

  /**
   * Convert screen coordinates to layer coordinates
   */
  screenToLayer(point: Point): Point {
    return this.viewportManager.screenToLayer(point);
  }

  /**
   * Convert layer coordinates to screen coordinates
   */
  layerToScreen(point: Point): Point {
    return this.viewportManager.layerToScreen(point);
  }

  /**
   * Get pointer position in layer coordinates
   */
  getPointerPosition(): Point | null {
    const pointer = this.stage.getPointerPosition();
    if (!pointer) return null;
    return this.screenToLayer(pointer);
  }

  // ============================================================================
  // EXPORT
  // ============================================================================

  /**
   * Export canvas to PNG data URL
   */
  toDataURL(options?: { pixelRatio?: number }): string {
    return this.stage.toDataURL({
      pixelRatio: options?.pixelRatio || 2,
      mimeType: 'image/png',
    });
  }

  /**
   * Export and download as PNG
   */
  downloadPNG(filename?: string): void {
    const dataUrl = this.toDataURL({ pixelRatio: 2 });
    const link = document.createElement('a');
    link.download = filename || `canvas-export-${Date.now()}.png`;
    link.href = dataUrl;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  // ============================================================================
  // STAGE ACCESS
  // ============================================================================

  /**
   * Get the Konva stage (for advanced operations)
   */
  getStage(): Konva.Stage {
    return this.stage;
  }

  /**
   * Get the main layer
   */
  getLayer(): Konva.Layer {
    return this.layer;
  }

  /**
   * Get canvas dimensions
   */
  getDimensions(): { width: number; height: number } {
    return { width: this.width, height: this.height };
  }

  /**
   * Resize the canvas
   */
  resize(width: number, height: number): void {
    this.width = width;
    this.height = height;
    this.stage.width(width);
    this.stage.height(height);
    
    // Update background size
    this.backgroundRect.width(width * 10);
    this.backgroundRect.height(height * 10);
    
    this.layer.batchDraw();
  }

  /**
   * Set cursor style
   */
  setCursor(cursor: string): void {
    this.stage.container().style.cursor = cursor;
  }

  /**
   * Force redraw
   */
  batchDraw(): void {
    this.layer.batchDraw();
  }

  // ============================================================================
  // STAGE EVENT HANDLERS (for edit mode)
  // ============================================================================

  /**
   * Set mouse down handler
   */
  onMouseDown(handler: (e: Konva.KonvaEventObject<MouseEvent>) => void): void {
    this.stage.on('mousedown', handler);
  }

  /**
   * Set mouse move handler
   */
  onMouseMove(handler: (e: Konva.KonvaEventObject<MouseEvent>) => void): void {
    this.stage.on('mousemove', handler);
  }

  /**
   * Set mouse up handler
   */
  onMouseUp(handler: (e: Konva.KonvaEventObject<MouseEvent>) => void): void {
    this.stage.on('mouseup', handler);
  }

  /**
   * Set context menu handler
   */
  onContextMenu(handler: (e: Konva.KonvaEventObject<PointerEvent>) => void): void {
    this.stage.on('contextmenu', handler);
  }

  /**
   * Remove mouse down handler
   */
  offMouseDown(): void {
    this.stage.off('mousedown');
  }

  /**
   * Remove mouse move handler
   */
  offMouseMove(): void {
    this.stage.off('mousemove');
  }

  /**
   * Remove mouse up handler
   */
  offMouseUp(): void {
    this.stage.off('mouseup');
  }

  /**
   * Remove context menu handler
   */
  offContextMenu(): void {
    this.stage.off('contextmenu');
  }

  // ============================================================================
  // CLEANUP
  // ============================================================================

  /**
   * Destroy the canvas and cleanup resources
   */
  destroy(): void {
    // Cleanup all renderers
    this.renderers.forEach((renderer) => {
      renderer.destroy();
    });
    this.renderers.clear();
    
    // Cleanup viewport manager
    this.viewportManager.destroy();
    
    // Cleanup event emitter
    this.emitter.removeAllListeners();
    
    // Destroy stage
    this.stage.destroy();
    
    // Cleanup pattern canvas
    this.backgroundPattern = null;
  }
}
